import { useState } from 'react'
import CheckboxList, { CheckboxItem } from './CheckboxList'
import CheckboxInput from './CheckboxInput'

/**
 * Recursively set descendants of the modified checkbox
 * to the new value.
 */
const updateCheckboxAndDescendants = (checkboxItem: CheckboxItem, checked: boolean): void => {
  checkboxItem.checked = checked
  if (!checkboxItem.children) return

  checkboxItem.children.forEach(childItem => updateCheckboxAndDescendants(childItem, checked))
}

/**
 * Select/Deselect all checkboxes recursively.
 */
const toggleAllCheckboxData = (checkboxData: CheckboxItem[], checked: boolean): void => {
  checkboxData.forEach(checkboxItem => {
    checkboxItem.checked = checked
    if (checkboxItem.children) {
      toggleAllCheckboxData(checkboxItem.children, checked)
    }
  })
}

/**
 * Update checkbox states based on the modified checkbox's new state.
 * Only direct ancestors of the modified checkbox are affected.
 */
const resolveCheckboxStates = (checkboxItem: CheckboxItem, indices: ReadonlyArray<number>): void => {
  if (indices.length > 0 && checkboxItem.children) {
    resolveCheckboxStates(checkboxItem.children[indices[0]], indices.slice(1))
  }

  if (!checkboxItem.children) return

  const checkedChildren = checkboxItem.children.reduce((total, item) => total + Number(item.checked === true), 0)
  const uncheckedChildren = checkboxItem.children.reduce((total, item) => total + Number(item.checked === false), 0)

  if (checkedChildren === checkboxItem.children.length) {
    checkboxItem.checked = true
  } else if (uncheckedChildren === checkboxItem.children.length) {
    checkboxItem.checked = false
  } else {
    checkboxItem.checked = 'indeterminate'
  }
}

/**
 * Check if all checkboxes are selected.
 */
const hasAllCheckboxDataSelected = (checkBoxData: ReadonlyArray<CheckboxItem>): boolean => {
  for (const checkBoxItem of checkBoxData) {
    if (!checkBoxItem.checked) return false
    if (checkBoxItem.children && !hasAllCheckboxDataSelected(checkBoxItem.children)) {
      return false
    }
  }
  return true
}

const Checkboxes = ({
  defaultCheckboxData,
  onChange
}: Readonly<{
  defaultCheckboxData: ReadonlyArray<CheckboxItem>
  onChange: (checkboxData: ReadonlyArray<CheckboxItem>) => void
}>) => {
  const [checkboxData, setCheckboxData] = useState(defaultCheckboxData)

  const toggleSelectAll = (checked: boolean) => {
    const newCheckboxData = JSON.parse(JSON.stringify(checkboxData))
    toggleAllCheckboxData(newCheckboxData, checked)
    setCheckboxData(newCheckboxData)
    onChange(newCheckboxData)
  }

  const areAllCheckboxesSelected = hasAllCheckboxDataSelected(checkboxData)

  return (
    <div>
      <div className='px-4 py-2 border-1 rounded-t-md border-gray-light'>
        <CheckboxInput
          id='checkbox-select-all'
          checked={areAllCheckboxesSelected}
          label='Select all'
          onChange={event => {
            event.stopPropagation()
            toggleSelectAll(event.target.checked)
          }}
          className='text-text-sm-medium'
        />
      </div>

      <CheckboxList
        items={checkboxData}
        onCheck={(checked, indices) => {
          const newCheckboxData = JSON.parse(JSON.stringify(checkboxData))

          const nonFirstLevelIndices = indices.slice(1)
          const modifiedCheckboxItem = nonFirstLevelIndices.reduce(
            (modifiedItem, index) => modifiedItem.children[index],
            newCheckboxData[indices[0]]
          )

          updateCheckboxAndDescendants(modifiedCheckboxItem, checked)
          resolveCheckboxStates(newCheckboxData[indices[0]], nonFirstLevelIndices)

          setCheckboxData(newCheckboxData)
          onChange(newCheckboxData)
        }}
      />
    </div>
  )
}

export default Checkboxes
